/**
 * NGAR2Service - Cliente para Worker NGA R2 (split por municipio)
 *
 * Gestiona la carga lazy de datos NGA (Nomenclátor Geográfico de Andalucía)
 * desde el Worker R2 con caché LRU en memoria y búsqueda fuzzy por municipio.
 *
 * Datos: ~300K topónimos en 785 municipios
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Ruta R2: /nga/{ine5}/nga-{ine5}.json.gz
 *
 * @module services/geocoding/specialized
 * @version 3.0.0
 * @date 2026-02-14
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  NgaR2Feature,
  NgaR2MunicipioData,
  NGAR2ServiceConfig,
  NGAR2ServiceStatus,
} from '../../../types/ngaR2';
import { PROVINCIAS_ANDALUCIA } from '../../../types/catastro';
import { fetchWithBackoff } from '../../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;
const DEFAULT_TIMEOUT = 10_000;  // 10s (archivos pequeños ~1-50KB)
const CACHE_MAX_ITEMS = 20;
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutos

/** Configuración uFuzzy optimizada para topónimos españoles */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 8,     // Topónimos largos: "Cortijo de la Cruz del Camino"
  interLft: 0,
  interRgt: 0,
};

// =============================================================================
// PREFIJOS PTEL → NGA (F3.08)
// =============================================================================

/**
 * Prefijos genéricos que los documentos PTEL anteponen a topónimos NGA.
 * Ejemplo PTEL: "Paraje Los Romerales" → NGA: "Los Romerales"
 * Orden: más largos primero para longest-match-first.
 */
const PTEL_PREFIX_PATTERNS: RegExp[] = [
  /^caser[ií]a\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^paraje\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^finca\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^lugar\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^sitio\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^pago\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
  /^partida\s+(?:de\s+(?:la\s+|los\s+|las\s+|el\s+)?)?/i,
];

/**
 * Genera variantes de artículos para topónimos.
 * "del Chopo" ↔ "El Chopo", "de los Romerales" ↔ "Los Romerales", etc.
 */
const ARTICLE_TRANSFORMS: Array<{ pattern: RegExp; replacement: string }> = [
  // "del X" → "El X"
  { pattern: /^del\s+/i, replacement: 'El ' },
  // "de la X" → "La X"
  { pattern: /^de la\s+/i, replacement: 'La ' },
  // "de los X" → "Los X"
  { pattern: /^de los\s+/i, replacement: 'Los ' },
  // "de las X" → "Las X"
  { pattern: /^de las\s+/i, replacement: 'Las ' },
  // "El X" → "del X"
  { pattern: /^El\s+/i, replacement: 'del ' },
  // "La X" → "de la X"
  { pattern: /^La\s+/i, replacement: 'de la ' },
  // "Los X" → "de los X"
  { pattern: /^Los\s+/i, replacement: 'de los ' },
  // "Las X" → "de las X"
  { pattern: /^Las\s+/i, replacement: 'de las ' },
];

/** NGACONF: palabras que no dicen nada del topónimo y no puntúan en `calculateMatchScore` */
const PALABRAS_VACIAS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'y', 'a', 'al', 'en', 'lo']);

/** NGACONF: longitud mínima de la palabra más corta para aceptar un acierto parcial por prefijo */
const LONGITUD_MINIMA_PARCIAL = 3;

/**
 * Normaliza query de topónimo PTEL generando variantes de búsqueda.
 *
 * 1. Elimina prefijos genéricos PTEL (paraje, finca, casería, pago, lugar, sitio, partida)
 * 2. Genera variantes de artículos (El ↔ del, Los ↔ de los, etc.)
 *
 * @returns Array de variantes únicas, original primero.
 */
export function normalizeToponymQuery(text: string): string[] {
  const trimmed = text.trim();
  if (trimmed.length < 2) return [trimmed];

  const variants = new Set<string>();
  variants.add(trimmed);

  // Paso 1: Eliminar prefijos PTEL
  let stripped = trimmed;
  for (const pattern of PTEL_PREFIX_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) {
      stripped = trimmed.slice(match[0].length).trim();
      if (stripped.length >= 2) {
        variants.add(stripped);
      }
      break; // Solo un prefijo puede aplicar
    }
  }

  // Paso 2: Variantes de artículos (sobre el texto limpio de prefijos)
  for (const transform of ARTICLE_TRANSFORMS) {
    if (transform.pattern.test(stripped)) {
      const variant = stripped.replace(transform.pattern, transform.replacement).trim();
      if (variant.length >= 2) {
        variants.add(variant);
      }
      break; // Solo una transformación de artículo por texto
    }
  }

  // Paso 3: Versión sin artículo (solo la raíz del topónimo)
  const withoutArticle = stripped
    .replace(/^(?:el|la|los|las|del|de la|de los|de las)\s+/i, '')
    .trim();
  if (withoutArticle.length >= 2 && withoutArticle !== stripped) {
    variants.add(withoutArticle);
  }

  return Array.from(variants);
}

// =============================================================================
// TIPOS INTERNOS
// =============================================================================

interface CacheEntry {
  data: NgaR2MunicipioData;
  nameIndex: string[];
  featureByName: Map<string, NgaR2Feature>;
  featureList: NgaR2Feature[];
  timestamp: number;
}

// =============================================================================
// SINGLETON SERVICE
// =============================================================================

let serviceInstance: NGAR2Service | null = null;

export function getNGAR2Service(config?: Partial<NGAR2ServiceConfig>): NGAR2Service {
  if (!serviceInstance) {
    serviceInstance = new NGAR2Service(config);
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetNGAR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class NGAR2Service {
  private readonly baseUrl: string;
  private readonly cache: Map<string, CacheEntry> = new Map();
  private readonly maxCacheSize: number;
  private readonly cacheTTL: number;
  private readonly timeout: number;
  private readonly debug: boolean;
  private readonly ufuzzy: uFuzzy;
  private loadingPromises: Map<string, Promise<NgaR2MunicipioData | null>> = new Map();
  private lastError: string | null = null;

  constructor(config?: Partial<NGAR2ServiceConfig>) {
    this.baseUrl = config?.baseUrl || WORKER_BASE_URL;
    this.maxCacheSize = config?.maxCacheSize ?? CACHE_MAX_ITEMS;
    this.cacheTTL = config?.cacheTTL ?? CACHE_TTL_MS;
    this.timeout = config?.timeout ?? DEFAULT_TIMEOUT;
    this.debug = config?.debug ?? false;
    this.ufuzzy = new uFuzzy(UFUZZY_OPTIONS);
  }

  // ===========================================================================
  // MÉTODOS PÚBLICOS
  // ===========================================================================

  /** Carga datos NGA de un municipio (lazy, con cache LRU) */
  async loadMunicipio(codINE: string): Promise<NgaR2MunicipioData | null> {
    if (!/^\d{5}$/.test(codINE)) {
      console.warn(`[NGAR2] Código INE inválido: ${codINE}`);
      return null;
    }

    const provincia = codINE.substring(0, 2);
    if (!PROVINCIAS_ANDALUCIA.includes(provincia as (typeof PROVINCIAS_ANDALUCIA)[number])) {
      console.warn(`[NGAR2] Provincia no andaluza: ${provincia}`);
      return null;
    }

    // Cache hit
    const cached = this.getFromCache(codINE);
    if (cached) return cached.data;

    // Dedup concurrent loads
    const existingPromise = this.loadingPromises.get(codINE);
    if (existingPromise) return existingPromise;

    const loadPromise = this.fetchMunicipioData(codINE);
    this.loadingPromises.set(codINE, loadPromise);

    try {
      const data = await loadPromise;
      if (data) {
        this.setInCache(codINE, data);
      }
      return data;
    } finally {
      this.loadingPromises.delete(codINE);
    }
  }

  /** Obtiene features de un municipio (carga si no está en cache) */
  async getFeaturesByMunicipio(codINE: string): Promise<NgaR2Feature[]> {
    const data = await this.loadMunicipio(codINE);
    if (!data) return [];
    const cached = this.getFromCache(codINE);
    return cached?.featureList ?? [];
  }

  /** Búsqueda fuzzy de topónimos en un municipio (two-pass: literal + normalizado) */
  async searchInMunicipio(
    codINE: string,
    query: string,
    maxResults = 5,
    minScore = 0.6
  ): Promise<Array<{ feature: NgaR2Feature; score: number }>> {
    // Cargar municipio si no está en cache
    await this.loadMunicipio(codINE);
    const cached = this.getFromCache(codINE);
    if (!cached || cached.nameIndex.length === 0) return [];

    // F3.08: Two-pass — literal primero, variantes normalizadas si falla
    const variants = normalizeToponymQuery(query);

    for (const variant of variants) {
      const results = this.searchSingleQuery(cached, variant, maxResults, minScore);
      if (results.length > 0) {
        this.log(`Match con variante "${variant}" (original: "${query}")`);
        return results;
      }
    }

    return [];
  }

  /** Búsqueda fuzzy interna para un solo query */
  private searchSingleQuery(
    cached: CacheEntry,
    query: string,
    maxResults: number,
    minScore: number
  ): Array<{ feature: NgaR2Feature; score: number }> {
    const normalizedQuery = this.normalizeName(query);
    if (normalizedQuery.length < 2) return [];

    // Búsqueda uFuzzy
    const [idxs, , order] = this.ufuzzy.search(cached.nameIndex, normalizedQuery);

    const results: Array<{ feature: NgaR2Feature; score: number }> = [];

    if (idxs && idxs.length > 0) {
      const sortedIdxs = order ? order.map(i => idxs[i]) : idxs;

      for (const idx of sortedIdxs) {
        if (results.length >= maxResults * 2) break;
        const feature = cached.featureList[idx];
        if (!feature) continue;

        const score = this.calculateMatchScore(normalizedQuery, cached.nameIndex[idx]);
        if (score >= minScore) {
          results.push({ feature, score });
        }
      }
    }

    // Fallback si uFuzzy no encuentra nada
    if (results.length === 0) {
      for (let i = 0; i < cached.nameIndex.length; i++) {
        const score = this.calculateMatchScore(normalizedQuery, cached.nameIndex[i]);
        if (score >= minScore) {
          results.push({ feature: cached.featureList[i], score });
        }
      }
      results.sort((a, b) => b.score - a.score);
    }

    return results.slice(0, maxResults);
  }

  /** Estado del servicio */
  getStatus(): NGAR2ServiceStatus {
    let totalFeatures = 0;
    this.cache.forEach((entry) => {
      totalFeatures += entry.featureList.length;
    });
    return {
      cachedMunicipios: this.cache.size,
      maxCacheSize: this.maxCacheSize,
      municipios: Array.from(this.cache.keys()),
      totalFeatures,
      error: this.lastError,
    };
  }

  /** Verifica si un municipio está en cache */
  hasMunicipio(codINE: string): boolean {
    return this.getFromCache(codINE) !== null;
  }

  /** Limpia toda la cache */
  clearCache(): void {
    this.cache.clear();
  }

  /** Estadísticas de cache */
  getCacheStats(): { size: number; maxSize: number; municipios: string[] } {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      municipios: Array.from(this.cache.keys()),
    };
  }

  // ===========================================================================
  // MÉTODOS PRIVADOS - Fetch
  // ===========================================================================

  private async fetchMunicipioData(codINE: string): Promise<NgaR2MunicipioData | null> {
    const url = `${this.baseUrl}/nga/${codINE}/nga-${codINE}.json.gz`;

    try {
      this.lastError = null;
      this.log(`Fetching: ${url}`);

      const { response } = await fetchWithBackoff(url, {
        timeoutMs: this.timeout,
        headers: { 'Accept': 'application/json' },
      });

      if (response.status === 404) {
        this.log(`Municipio sin datos NGA: ${codINE}`);
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // ─── Defensa en profundidad: detección proactiva por magic bytes ───
      // Lee el body UNA sola vez como ArrayBuffer y decide por contenido,
      // independientemente de cabeceras HTTP (que proxies pueden alterar).
      // Gzip RFC 1952: bytes 0-1 = 1F 8B (firma), byte 2 = 08 (deflate).
      const buffer = await response.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      const isGzip = bytes.length >= 3
        && bytes[0] === 0x1F
        && bytes[1] === 0x8B
        && bytes[2] === 0x08;

      let rawData: Record<string, unknown>;
      if (isGzip) {
        // Bytes gzip crudos → descomprimir con API nativa
        this.log(`Detectado gzip por magic bytes para ${codINE}`);
        if (typeof DecompressionStream === 'undefined') {
          throw new Error('DecompressionStream no disponible en este entorno');
        }
        const blob = new Blob([buffer]);
        const decompressed = blob.stream().pipeThrough(new DecompressionStream('gzip'));
        const text = await new Response(decompressed).text();
        rawData = JSON.parse(text);
      } else {
        // JSON limpio (Content-Encoding ya fue procesado por fetch)
        const text = new TextDecoder().decode(buffer);
        rawData = JSON.parse(text);
      }

      // Mapear a tipo interno
      const featuresRaw = (rawData.features as Array<Record<string, unknown>>) || [];
      const data: NgaR2MunicipioData = {
        version: (rawData.version as string) || '',
        source: (rawData.source as string) || '',
        endpoint: (rawData.endpoint as string) || '',
        crs: 'EPSG:25830',
        license: (rawData.license as string) || '',
        totalFeatures: (rawData.totalFeatures as number) || featuresRaw.length,
        withCodINE: (rawData.withCodINE as number) || 0,
        codINE: (rawData.codINE as string) || codINE,
        features: featuresRaw.map((f) => ({
          id: String(f.id || ''),
          name: (f.name as string) || '',
          type: (f.type as string) || '',
          x: f.x as number,
          y: f.y as number,
          codINE: (f.codINE as string) || codINE,
          municipio: (f.municipio as string) || '',
          provincia: (f.provincia as string) || '',
        })),
      };

      this.log(`Cargado ${codINE}: ${data.features.length} topónimos`);
      return data;

    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.lastError = `Error cargando NGA ${codINE}: ${message}`;
      console.error('[NGAR2]', this.lastError);
      return null;
    }
  }

  // ===========================================================================
  // MÉTODOS PRIVADOS - Cache LRU
  // ===========================================================================

  private getFromCache(codINE: string): CacheEntry | null {
    const entry = this.cache.get(codINE);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(codINE);
      return null;
    }

    return entry;
  }

  private setInCache(codINE: string, data: NgaR2MunicipioData): void {
    // Evict LRU si es necesario
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    // Construir índices de nombres para uFuzzy
    const nameIndex: string[] = [];
    const featureByName = new Map<string, NgaR2Feature>();
    const featureList: NgaR2Feature[] = [];

    for (const feature of data.features) {
      const normalizedName = this.normalizeName(feature.name);
      nameIndex.push(normalizedName);
      featureByName.set(normalizedName, feature);
      featureList.push(feature);
    }

    this.cache.set(codINE, {
      data,
      nameIndex,
      featureByName,
      featureList,
      timestamp: Date.now(),
    });
  }

  // ===========================================================================
  // MÉTODOS PRIVADOS - Normalización y scoring
  // ===========================================================================

  /** Normaliza nombre para búsqueda fuzzy */
  private normalizeName(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // Eliminar diacríticos
      .replace(/[^\w\s]/g, ' ')         // Puntuación → espacios
      .replace(/\s+/g, ' ')             // Colapsar espacios
      .trim();
  }

  /** Calcula score de similitud entre query y candidato */
  private calculateMatchScore(query: string, candidate: string): number {
    if (query === candidate) return 1.0;

    // NGACONF: solo puntúan las palabras con contenido. «de», «la», «y», «a» contaban como
    // aciertos y «La Asomadilla» casaba con «Camino de la Guardia de Jaén a Mancha Real».
    const conContenido = (s: string) =>
      s.split(' ').filter(w => w.length > 0 && !PALABRAS_VACIAS.has(w));
    const queryWords = conContenido(query);
    const candidateWords = conContenido(candidate);
    if (queryWords.length === 0) return 0;

    let matchedWords = 0;
    let partialMatches = 0;

    for (const qWord of queryWords) {
      if (candidateWords.includes(qWord)) {
        matchedWords++;
      } else {
        // NGACONF: el acierto parcial exige un prefijo de 3 letras o más
        // («latitud» empieza por «la», «atalaya» por la «a» de «A-4076»).
        const hasPartial = candidateWords.some(
          cWord =>
            Math.min(cWord.length, qWord.length) >= LONGITUD_MINIMA_PARCIAL &&
            (cWord.startsWith(qWord) || qWord.startsWith(cWord))
        );
        if (hasPartial) partialMatches++;
      }
    }

    const exactRecall = matchedWords / queryWords.length;
    const partialRecall = (partialMatches / queryWords.length) * 0.7;
    const substringBonus = candidate.includes(query) ? 0.15 : 0;

    return Math.min(1.0, exactRecall + partialRecall + substringBonus);
  }

  private log(message: string): void {
    if (this.debug) {
      console.log(`[NGAR2] ${message}`);
    }
  }
}

export default NGAR2Service;
